import { useTranslation } from 'react-i18next';
import {
  CompanyPlanUpgradeFeatureEnum,
  AccessComponentEnum,
  AccessLevelEnum,
} from 'graphql-common';
import { GetUrlParams, ORDERING_DIRECTION_DESC } from '@lib/enums/urls';
import useTabParam from '@lib/hooks/useTabParam';
import useIsOnline from '@lib/hooks/useIsOnline';
import FeatureLockState from '@lib/components/FeatureLockState';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Trans from '@lib/components/Trans/Trans';
import { PlanState } from '@lib/enums/plan';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import getStatusFromError from '@lib/utils/getStatusFromError';
import {
  isForbiddenError,
  isUpgradePlanRequiredError,
} from '@lib/utils/graphQLErrors';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import { DocumentTabEnum } from './enums';
import Documents from './components/Documents';
import useDocumentsActions from './utils/useDocumentsActions';

function DocumentsContainer() {
  const { t } = useTranslation();
  const isOnline = useIsOnline();
  const { getUserAccessByCompany, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  // Cache-derived: `whoami` can lag a plan change made in another session
  // (e.g. SystemAdmin disabling the feature mid-session), so this can still
  // read `false` for a moment after the company plan was actually locked.
  const isAccessLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;

  // During navigate() to another route, `use-query-params` re-renders this
  // container with the destination route's URL params *before* React Router
  // commits the unmount (its setState-driven context lags one render).
  // `useMatch` / `useLocation` lag too, but `use-query-params` reads
  // `window.location` directly via its ReactRouter6Adapter, so the in-flight
  // variables already reflect the new URL while the React Router match is
  // still stale. Read `window.location.pathname` here so the gate stays in
  // sync with `useQueryParams`, and the query is skipped during the
  // transition render (e.g. Scanner "Find tasks" navigating to
  // /perform-tasks which sets ordering_field=deadlineAt — not a valid
  // DocumentationSortInputObject field).
  const isRouteMatched =
    typeof window !== 'undefined' &&
    window.location.pathname === APP_URLS.app.documentation.index.path;

  const [tabParamRaw, setTabParam] = useTabParam({
    defaultTab: DocumentTabEnum.All,
    defaultPaginationParams: {
      [GetUrlParams.OrderingField]: 'updatedAt',
      [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_DESC,
    },
  });
  const tabParam = tabParamRaw ?? DocumentTabEnum.All;

  const {
    allCount,
    documentationsError,
    closeFileViewer,
    closeVersionNotes,
    collection,
    favoritesCount,
    fileViewerDocument,
    filterCounter,
    filterFields,
    filterParams,
    firstLoading,
    loading,
    onDetails,
    onToggleFavorite,
    onVersionNotes,
    onViewDocument,
    setFilterParams,
    totalCount,
    unreadCount,
    versionNotesDocument,
  } = useDocumentsActions({
    tabParam,
    // Offline: never fire the query — there's no local cache for documents
    // like there is for Tasks, so this view is locked instead.
    skip: !isRouteMatched || isAccessLocked || !isOnline,
  });

  // A FORBIDDEN_ERROR (or UPGRADE_PLAN_REQUIRED) on this query means the
  // access check above raced a plan change and read stale data — treat it as
  // locked instead of falling through to the generic error page below.
  const isViewLocked =
    isAccessLocked ||
    isForbiddenError(documentationsError) ||
    isUpgradePlanRequiredError(documentationsError);

  if (!isOnline) {
    return <ErrorPage status={ErrorPageStatusEnum.Status503} />;
  }

  if (isViewLocked) {
    return (
      <PageBody withoutHeader>
        <FeatureLockState
          title={t('lock-state-title-documentation')}
          text={<Trans i18nKey="lock-state-text-documentation" />}
          onUpgrade={() =>
            handleOpenPlanModal(PlanState.Locked, [
              CompanyPlanUpgradeFeatureEnum.Documentation,
            ])
          }
        />
      </PageBody>
    );
  }

  if (documentationsError) {
    return (
      <ErrorPage
        status={getStatusFromError(documentationsError)}
        description={getDescriptionFromError(documentationsError)}
      />
    );
  }

  return (
    <Documents
      allCount={allCount}
      closeFileViewer={closeFileViewer}
      closeVersionNotes={closeVersionNotes}
      collection={collection}
      favoritesCount={favoritesCount}
      fileViewerDocument={fileViewerDocument}
      filterCounter={filterCounter}
      filterFields={filterFields}
      filterParams={filterParams}
      firstLoading={firstLoading}
      loading={loading}
      onDetails={onDetails}
      onTabParamChange={setTabParam}
      onToggleFavorite={onToggleFavorite}
      onVersionNotes={onVersionNotes}
      onViewDocument={onViewDocument}
      setFilterParams={setFilterParams}
      tabParam={tabParam}
      totalCount={totalCount}
      unreadCount={unreadCount}
      versionNotesDocument={versionNotesDocument}
    />
  );
}

DocumentsContainer.displayName = `${AccessComponentEnum.DocumentationManagement}-DocumentsContainer`;

export default DocumentsContainer;
